import { useState, useRef, useCallback } from 'react';
import {
    Upload, X, FileText, Image, Camera, Paperclip,
    Eye, Download, CheckCircle2, AlertCircle
} from 'lucide-react';
import clsx from 'clsx';

/**
 * MediaUpload — Görsel ve PDF yükleme komponenti
 *
 * Props:
 * @param {('single'|'multiple')} mode         - Tek dosya mı, çoklu mu? (default: 'single')
 * @param {('image'|'pdf'|'all')} accept       - Kabul edilen dosya tipleri (default: 'all')
 * @param {number} maxSizeMB                   - Maksimum dosya boyutu MB (default: 10)
 * @param {number} maxFiles                    - Çoklu modda maksimum dosya sayısı (default: 8)
 * @param {string} label                       - Başlık (default tip'e göre otomatik)
 * @param {string} hint                        - Alt açıklama
 * @param {boolean} compact                    - Modal içi küçük görünüm (default: false)
 * @param {boolean} showCompressionNote        - "Görsel sıkıştırılır" notu (default: true)
 * @param {(File[]) => void} onFilesChange     - Dosya değişiminde callback
 * @param {string[]} initialUrls               - Mevcut dosya URL'leri (edit modunda)
 */
export default function MediaUpload({
    mode = 'single',
    accept = 'all',
    maxSizeMB = 10,
    maxFiles = 8,
    label,
    hint,
    compact = false,
    showCompressionNote = true,
    onFilesChange,
    initialUrls = [],
}) {
    const [files, setFiles] = useState([]);
    const [existingUrls] = useState(initialUrls);
    const [isDragging, setIsDragging] = useState(false);
    const [errors, setErrors] = useState([]);
    const [previewFile, setPreviewFile] = useState(null);
    const fileInputRef = useRef(null);

    // -- Kabul edilen mime tipler
    const getAcceptString = () => {
        switch (accept) {
            case 'image': return 'image/jpeg,image/png,image/webp';
            case 'pdf':   return 'application/pdf';
            default:      return 'image/jpeg,image/png,image/webp,application/pdf';
        }
    };

    const getAutoLabel = () => {
        if (label) return label;
        switch (accept) {
            case 'image': return 'Görsel Yükle';
            case 'pdf':   return 'PDF Yükle';
            default:      return 'Belge / Görsel Yükle';
        }
    };

    const getAutoHint = () => {
        if (hint) return hint;
        switch (accept) {
            case 'image': return 'JPG, PNG, WebP — maks. ' + maxSizeMB + 'MB';
            case 'pdf':   return 'PDF — maks. ' + maxSizeMB + 'MB';
            default:      return 'JPG, PNG, PDF — maks. ' + maxSizeMB + 'MB';
        }
    };

    // -- Dosya doğrulama
    const validateFile = (file) => {
        const sizeOk = file.size / 1024 / 1024 <= maxSizeMB;
        if (!sizeOk) return `${file.name}: En fazla ${maxSizeMB}MB olabilir`;

        const isImage = file.type.startsWith('image/');
        const isPDF   = file.type === 'application/pdf';
        if (accept === 'image' && !isImage) return `${file.name}: Sadece görsel yüklenebilir`;
        if (accept === 'pdf'   && !isPDF)   return `${file.name}: Sadece PDF yüklenebilir`;
        if (accept === 'all'   && !isImage && !isPDF) return `${file.name}: Desteklenmeyen dosya tipi`;
        return null;
    };

    // -- Dosya ekleme işlemi
    const addFiles = useCallback((incoming) => {
        const newErrors = [];
        const validated = [];

        Array.from(incoming).forEach((file) => {
            const err = validateFile(file);
            if (err) { newErrors.push(err); return; }
            validated.push(file);
        });

        setErrors(newErrors);

        setFiles((prev) => {
            let next;
            if (mode === 'single') {
                next = validated.slice(0, 1);
            } else {
                const combined = [...prev, ...validated];
                next = combined.slice(0, maxFiles);
                if (combined.length > maxFiles) {
                    newErrors.push(`En fazla ${maxFiles} dosya eklenebilir.`);
                }
            }
            onFilesChange?.(next);
            return next;
        });
    }, [mode, maxFiles, maxSizeMB, accept, onFilesChange]); // eslint-disable-line

    const removeFile = (idx) => {
        setFiles((prev) => {
            const next = prev.filter((_, i) => i !== idx);
            onFilesChange?.(next);
            return next;
        });
    };

    // -- Drag & Drop
    const onDragOver  = (e) => { e.preventDefault(); setIsDragging(true); };
    const onDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
    const onDrop      = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };
    const onInputChange = (e) => { addFiles(e.target.files); e.target.value = ''; };

    // -- Dosya önizleme URL'si
    const getPreviewUrl = (file) => {
        if (file.type.startsWith('image/')) return URL.createObjectURL(file);
        return null;
    };

    // -- Dosya ikonu
    const FileIcon = ({ file, size = 20 }) => {
        if (file.type.startsWith('image/')) return <Image size={size} className="text-primary" />;
        if (file.type === 'application/pdf') return <FileText size={size} className="text-error" />;
        return <Paperclip size={size} className="text-text-muted" />;
    };

    const hasContent = files.length > 0 || existingUrls.length > 0;
    const canAddMore = mode === 'multiple'
        ? (files.length + existingUrls.length) < maxFiles
        : files.length === 0 && existingUrls.length === 0;

    const dropZoneHeight = compact ? 'py-4' : 'py-8';

    return (
        <div className="w-full space-y-3">
            {/* Drop zone — sadece daha dosya eklenebiliyorsa göster */}
            {canAddMore && (
                <div
                    className={clsx(
                        'border-2 border-dashed rounded-xl text-center transition-all duration-200 cursor-pointer',
                        dropZoneHeight,
                        isDragging
                            ? 'border-primary bg-primary/5 scale-[1.01]'
                            : 'border-border-light hover:border-primary/60 hover:bg-bg-tertiary',
                    )}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept={getAcceptString()}
                        multiple={mode === 'multiple'}
                        onChange={onInputChange}
                        capture={accept === 'image' ? 'environment' : undefined}
                    />

                    {/* İkon grubu */}
                    <div className={clsx(
                        'flex items-center justify-center gap-2 mx-auto mb-3',
                        compact ? 'text-text-muted' : ''
                    )}>
                        {accept === 'image' ? (
                            <>
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                    <Image size={20} className="text-primary" />
                                </div>
                                <div className="w-8 h-8 rounded-full bg-bg-secondary border border-border-light flex items-center justify-center -ml-3">
                                    <Camera size={14} className="text-text-muted" />
                                </div>
                            </>
                        ) : accept === 'pdf' ? (
                            <div className="w-10 h-10 rounded-full bg-error/10 flex items-center justify-center">
                                <FileText size={20} className="text-error" />
                            </div>
                        ) : (
                            <>
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                                    <Image size={20} className="text-primary" />
                                </div>
                                <div className="w-8 h-8 rounded-full bg-error/10 flex items-center justify-center -ml-2">
                                    <FileText size={14} className="text-error" />
                                </div>
                            </>
                        )}
                    </div>

                    <p className={clsx(
                        'font-semibold text-text-primary',
                        compact ? 'text-sm' : 'text-base'
                    )}>
                        {getAutoLabel()}
                    </p>
                    <p className="text-xs text-text-tertiary mt-1">{getAutoHint()}</p>

                    {!compact && mode === 'multiple' && (
                        <p className="text-xs text-text-muted mt-1">
                            En fazla {maxFiles} dosya • Şu an: {files.length + existingUrls.length}
                        </p>
                    )}
                </div>
            )}

            {/* Hata mesajları */}
            {errors.length > 0 && (
                <div className="space-y-1">
                    {errors.map((err, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-error bg-error/5 border border-error/20 rounded-lg px-3 py-2">
                            <AlertCircle size={14} />
                            {err}
                        </div>
                    ))}
                </div>
            )}

            {/* Dosya listesi */}
            {hasContent && (
                <div className={clsx(
                    'space-y-2',
                    mode === 'multiple' && files.length > 0 ? 'grid grid-cols-1 gap-2' : ''
                )}>
                    {/* Mevcut URL'ler (edit modu) */}
                    {existingUrls.map((url, idx) => (
                        <div
                            key={`existing-${idx}`}
                            className="flex items-center gap-3 p-3 bg-bg-tertiary rounded-lg border border-border-light"
                        >
                            <div className="w-9 h-9 rounded-lg bg-success/10 flex items-center justify-center shrink-0">
                                <CheckCircle2 size={18} className="text-success" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-text-primary truncate">
                                    Yüklü dosya {idx + 1}
                                </p>
                                <p className="text-[10px] text-text-muted">Sunucuda mevcut</p>
                            </div>
                            <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-1.5 hover:bg-bg-secondary rounded-lg text-text-muted hover:text-primary transition-colors"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Eye size={15} />
                            </a>
                        </div>
                    ))}

                    {/* Yeni seçilen dosyalar */}
                    {files.map((file, idx) => {
                        const isImage = file.type.startsWith('image/');
                        const previewUrl = isImage ? getPreviewUrl(file) : null;

                        return (
                            <div
                                key={idx}
                                className="flex items-center gap-3 p-3 bg-bg-tertiary rounded-lg border border-border-light group"
                            >
                                {/* Thumbnail veya ikon */}
                                <div className="w-9 h-9 rounded-lg overflow-hidden shrink-0 bg-bg-secondary flex items-center justify-center border border-border-light">
                                    {previewUrl ? (
                                        <img
                                            src={previewUrl}
                                            alt={file.name}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <FileIcon file={file} size={18} />
                                    )}
                                </div>

                                {/* Dosya adı ve boyutu */}
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-text-primary truncate">{file.name}</p>
                                    <p className="text-[10px] text-text-muted">
                                        {(file.size / 1024 / 1024).toFixed(2)} MB
                                        {isImage && (
                                            <span className="ml-1 text-primary/70">• Sıkıştırılacak</span>
                                        )}
                                    </p>
                                </div>

                                {/* Aksiyon butonları */}
                                <div className="flex items-center gap-1">
                                    {previewUrl && (
                                        <button
                                            type="button"
                                            onClick={() => setPreviewFile(previewFile === idx ? null : idx)}
                                            className="p-1.5 text-text-muted hover:text-primary hover:bg-bg-secondary rounded-lg transition-colors"
                                        >
                                            <Eye size={15} />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => removeFile(idx)}
                                        className="p-1.5 text-text-muted hover:text-error hover:bg-error/5 rounded-lg transition-colors"
                                    >
                                        <X size={15} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}

                    {/* Inline görsel önizleme */}
                    {previewFile !== null && files[previewFile]?.type.startsWith('image/') && (
                        <div className="rounded-xl overflow-hidden border border-border-light bg-black/5">
                            <img
                                src={getPreviewUrl(files[previewFile])}
                                alt="Önizleme"
                                className="w-full max-h-64 object-contain"
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Sıkıştırma notu */}
            {showCompressionNote && accept !== 'pdf' && !compact && (
                <p className="text-[10px] text-text-muted leading-relaxed px-1">
                    Görseller yüklemeden önce otomatik olarak sıkıştırılır (300–500 KB hedef). Yazılar okunabilir kalır.
                </p>
            )}

            {/* Çoklu modda "daha ekle" butonu — drop zone dolunca bu gösterilir */}
            {mode === 'multiple' && !canAddMore && (
                <p className="text-xs text-text-muted text-center py-1">
                    Maksimum {maxFiles} dosya sınırına ulaşıldı.
                </p>
            )}
        </div>
    );
}
